import {
  DEFAULT_FIRST_WINDOW_PCT,
  DEFAULT_LAST_WINDOW_HOURS,
  computeTrendDrift,
} from "@/components/root-cause-analysis/pre-shutdown-drift-summary.jsx";

/**
 * @param {unknown} n
 * @returns {number}
 */
function toFiniteNumber(n) {
  const x = Number(n);
  return Number.isFinite(x) ? x : 0;
}

/**
 * @param {import("@/components/root-cause-analysis/rca-measurement-trend-shared.jsx").MeasurementWithStatsRow|Record<string, unknown>} row
 * @param {{ meanFirst: number, meanLast: number, drift: number }} driftResult
 * @returns {import("@/api/rca-api.js").RCAiTrendSummary}
 */
function mapRowToTrendSummary(row, driftResult) {
  const tagDescription = String(row.TagDescription ?? "").trim();

  return {
    assetTagId: toFiniteNumber(row.AssetTagId),
    universalDescriptor: tagDescription,
    tagName: tagDescription ? "" : `Tag ${row.AssetTagId ?? ""}`,
    uom: String(row.UoM ?? "").trim(),
    avg: toFiniteNumber(row.Avg),
    min: toFiniteNumber(row.Min),
    max: toFiniteNumber(row.Max),
    thresholds: {
      lo: toFiniteNumber(row.Lo),
      lolo: toFiniteNumber(row.LoLo),
      hi: toFiniteNumber(row.Hi),
      hihi: toFiniteNumber(row.HiHi),
    },
    chars: {
      win1Mean: toFiniteNumber(driftResult.meanFirst),
      win2Mean: toFiniteNumber(driftResult.meanLast),
      driftPct: Number.isFinite(driftResult.drift)
        ? driftResult.drift * 100
        : 0,
    },
  };
}

/**
 * Build the RCA AI request payload from measurement rows shown on the RCA screen.
 *
 * @param {{
 *   downtimeEventId: number,
 *   startDate: string,
 *   requestId: string,
 *   assetId: string|number,
 *   trendWindowHours: number,
 *   rows: unknown[],
 *   firstWindowPct?: number,
 *   lastWindowHours?: number,
 * }} options
 * @returns {import("@/api/rca-api.js").RCAiRequest}
 */
export function buildRcaAiRequest({
  downtimeEventId,
  startDate,
  requestId,
  assetId,
  trendWindowHours,
  alarmAndDowntimeLimit,
  rows,
  firstWindowPct = DEFAULT_FIRST_WINDOW_PCT,
  lastWindowHours = DEFAULT_LAST_WINDOW_HOURS,
}) {
  const trendWindow = toFiniteNumber(trendWindowHours);
  const win1mins = (trendWindow * 60 * firstWindowPct) / 100;
  const win2mins = toFiniteNumber(lastWindowHours) * 60;

  const driftConfig = { firstWindowPct, lastWindowHours };

  const summary = (Array.isArray(rows) ? rows : []).map((row) => {
    const driftResult = computeTrendDrift(row, driftConfig);
    return mapRowToTrendSummary(row, driftResult);
  });

  return {
    downtimeEventId: downtimeEventId,
    DowntimeEventStartDate: startDate,
    id: requestId,
    assetId: assetId,
    trendWindow,
    alarmAndDowntimeLimit,
    win1mins,
    win2mins,
    summary,
  };
}
